import { useEffect, useMemo, useState } from 'react'
import { supabase } from './supabaseClient'
import EmptyState from './ui/EmptyState'
import ErrorState from './ui/ErrorState'
import LoadingState from './ui/LoadingState'
import {
  TemplateFormShell,
  TemplateHeaderBlock,
  TemplatePage,
  TemplateSectionCard,
  TemplateSummaryRow,
  TemplateTableShell
} from './ui/PageTemplate'
import StatPill from './ui/StatPill'
import StatusBadge from './ui/StatusBadge'
import { ROLE_OPTIONS_EXPORT, VALID_ROLES, normalizeRoleForStorage, normalizeRoleForForm, formatRoleLabel } from './utils/roleFormatting'
// Phase 3B3: Form hooks
import { useForm } from './hooks/useForm'
import { useFormMessage } from './hooks/useFormMessage'
import { useFormSubmit } from './hooks/useFormSubmit'

const ROLE_OPTIONS = ROLE_OPTIONS_EXPORT

const buildEmptyForm = () => ({
  name: '',
  phone: '',
  role: 'employee'
})

export default function WorkersPage() {
  const [workers, setWorkers] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  const [editorOpen, setEditorOpen] = useState(false)
  const [editorMode, setEditorMode] = useState('create')
  const [editingWorkerId, setEditingWorkerId] = useState(null)

  // Phase 3B3: Replace manual form state with hooks
  const form = useForm({ initialValues: buildEmptyForm() })
  const formMessage = useFormMessage()
  const formSubmit = useFormSubmit({
    onSubmit: async (values) => {
      const name = values.name.trim()
      const phone = values.phone.trim()
      const role = normalizeRoleForStorage(values.role)

      if (!name) {
        throw new Error('Name is required.')
      }

      if (!VALID_ROLES.has(role)) {
        throw new Error('Select a valid role before saving.')
      }

      if (editorMode === 'edit' && editingWorkerId) {
        const { error } = await supabase
          .from('workers')
          .update({ name, phone: phone || null, role })
          .eq('id', editingWorkerId)

        if (error) {
          throw new Error('Unable to update worker: ' + error.message)
        }

        return { action: 'update', name }
      } else {
        const { error } = await supabase
          .from('workers')
          .insert([{ name, phone: phone || null, role }])

        if (error) {
          throw new Error('Unable to add worker: ' + error.message)
        }

        return { action: 'create', name }
      }
    },
    onSuccess: async (result) => {
      await loadWorkers({ showSpinner: false })
      form.reset()
      setEditorMode('create')
      setEditingWorkerId(null)
      formMessage.setSuccess(result.action === 'update' ? 'Worker updated successfully.' : 'Worker added successfully.')
    },
    onError: (error) => {
      formMessage.setError(error.message)
    }
  })

  const [deactivateBusyWorkerId, setDeactivateBusyWorkerId] = useState(null)
  const [directoryFeedback, setDirectoryFeedback] = useState('')

  const loadWorkers = async ({ isMounted = () => true, showSpinner = true } = {}) => {
    if (showSpinner) setLoading(true)
    setLoadError('')

    try {
      const { data, error } = await supabase
        .from('workers')
        .select('*')
        .order('name', { ascending: true })

      if (!isMounted()) return

      if (error) {
        console.error('Error loading workers', error)
        setWorkers([])
        setLoadError('Unable to load workers right now. Please retry.')
        return
      }

      setWorkers(data || [])
    } catch (error) {
      if (!isMounted()) return
      console.error('Unexpected workers load error', error)
      setWorkers([])
      setLoadError('Unexpected error loading workers. Please retry.')
    } finally {
      if (isMounted()) setLoading(false)
    }
  }

  useEffect(() => {
    let mounted = true

    loadWorkers({ isMounted: () => mounted })

    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    if (!loading && !loadError && workers.length === 0) {
      setEditorOpen(true)
    }
  }, [loading, loadError, workers.length])

  const stats = useMemo(() => {
    const total = workers.length
    const active = workers.filter((worker) => worker.is_active !== false).length
    const inactive = total - active
    const shiftLeads = workers.filter((worker) => normalizeRoleForStorage(worker.role) === 'shift_lead').length
    const admins = workers.filter((worker) => normalizeRoleForStorage(worker.role) === 'admin').length

    return {
      total,
      active,
      inactive,
      shiftLeads,
      admins
    }
  }, [workers])

  const resetEditor = ({ close = false } = {}) => {
    setEditorMode('create')
    setEditingWorkerId(null)
    form.reset()
    formMessage.clear()
    if (close) setEditorOpen(false)
  }

  const openCreate = () => {
    resetEditor()
    setEditorOpen(true)
  }

  const openEdit = (worker) => {
    if (!worker) return
    setEditorMode('edit')
    setEditingWorkerId(worker.id)
    form.setValues({
      name: worker.name || '',
      phone: worker.phone || '',
      role: normalizeRoleForForm(worker.role)
    })
    formMessage.clear()
    setEditorOpen(true)
  }

  const handleFormSubmit = async (event) => {
    event.preventDefault()
    await formSubmit.submit(form.values)
  }

  const handleDeactivate = async (workerId) => {
    const target = workers.find((worker) => worker.id === workerId)
    if (!target || target.is_active === false) return

    setDirectoryFeedback('')
    setDeactivateBusyWorkerId(workerId)

    try {
      const { error } = await supabase
        .from('workers')
        .update({ is_active: false })
        .eq('id', workerId)

      if (error) {
        console.error('Error deactivating worker', error)
        setDirectoryFeedback('Unable to deactivate worker: ' + error.message)
        return
      }

      setWorkers((currentWorkers) => currentWorkers.map((worker) => {
        if (worker.id !== workerId) return worker
        return {
          ...worker,
          is_active: false
        }
      }))
    } catch (error) {
      console.error('Unexpected deactivate error', error)
      setDirectoryFeedback('Unexpected deactivation error. Please retry.')
    } finally {
      setDeactivateBusyWorkerId(null)
    }
  }

  return (
    <TemplatePage className="gg-workers-canonical" data-testid="active-view-workers">
      <TemplateHeaderBlock
        eyebrow="Workforce"
        title="Workers"
        description="Manage onboarding, role assignment, and active status from one canonical workspace surface."
        actions={(
          <button type="button" className="gg-button gg-button--primary" onClick={openCreate}>
            Add Worker
          </button>
        )}
        className="gg-workers-canonical__hero"
      >
        <TemplateSummaryRow className="gg-workers-summary-grid">
          <article className="gg-card gg-workers-summary-card">
            <p className="gg-workers-summary-card__label">Total Workers</p>
            <p className="gg-workers-summary-card__value">{stats.total}</p>
            <StatPill label="Directory" value={stats.total} tone="highlight" />
          </article>

          <article className="gg-card gg-workers-summary-card">
            <p className="gg-workers-summary-card__label">Active</p>
            <p className="gg-workers-summary-card__value">{stats.active}</p>
            <StatPill label="Inactive" value={stats.inactive} tone={stats.inactive > 0 ? 'warning' : 'success'} />
          </article>

          <article className="gg-card gg-workers-summary-card">
            <p className="gg-workers-summary-card__label">Leadership</p>
            <p className="gg-workers-summary-card__value">{stats.shiftLeads + stats.admins}</p>
            <div className="gg-workers-summary-card__stack">
              <span className="gg-inline-chip">Shift Leads {stats.shiftLeads}</span>
              <span className="gg-inline-chip">Admins {stats.admins}</span>
            </div>
          </article>
        </TemplateSummaryRow>
      </TemplateHeaderBlock>

      {editorOpen ? (
        <TemplateSectionCard
          label={editorMode === 'edit' ? 'Edit Worker' : 'Add Worker'}
          title={editorMode === 'edit' ? 'Update Worker Details' : 'Create New Worker'}
          subtitle="Capture key worker details and default role for this workspace."
          data-testid="workers-editor"
          className="gg-workers-canonical__editor"
        >
          <TemplateFormShell>
            <form className="gg-form-grid gg-form-grid--split gg-workers-editor-form" onSubmit={handleFormSubmit}>
              <label className="gg-form-field gg-form-field--wide" htmlFor="workers-form-name">
                <span>Name *</span>
                <input
                  id="workers-form-name"
                  className="gg-input"
                  value={form.values.name}
                  onChange={(event) => form.setField('name', event.target.value)}
                  placeholder="Full name"
                />
              </label>

              <label className="gg-form-field" htmlFor="workers-form-phone">
                <span>Phone</span>
                <input
                  id="workers-form-phone"
                  className="gg-input"
                  value={form.values.phone}
                  onChange={(event) => form.setField('phone', event.target.value)}
                  placeholder="Phone number"
                />
              </label>

              <label className="gg-form-field" htmlFor="workers-form-role">
                <span>Role</span>
                <select
                  id="workers-form-role"
                  className="gg-select"
                  value={form.values.role}
                  onChange={(event) => form.setField('role', event.target.value)}
                >
                  {ROLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <div className="gg-form-actions gg-form-field--wide">
                <button type="submit" className="gg-button gg-button--primary" disabled={formSubmit.isSubmitting}>
                  {formSubmit.isSubmitting ? 'Saving...' : editorMode === 'edit' ? 'Save Worker' : 'Add Worker'}
                </button>
                {editorMode === 'edit' ? (
                  <button type="button" className="gg-button gg-button--ghost" onClick={openCreate}>
                    Cancel Edit
                  </button>
                ) : null}
                <button type="button" className="gg-button gg-button--secondary" onClick={() => resetEditor({ close: true })}>
                  Hide Form
                </button>
              </div>
            </form>
          </TemplateFormShell>

          {formMessage.message ? <p className={`gg-inline-alert ${formMessage.tone}`}>{formMessage.message}</p> : null}
        </TemplateSectionCard>
      ) : null}

      <TemplateSectionCard
        label="Worker Directory"
        title="Directory"
        subtitle="Review status, update details, and deactivate workers when needed."
        actions={(
          <button type="button" className="gg-button gg-button--secondary" onClick={() => loadWorkers()} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        )}
        className="gg-workers-canonical__directory"
      >
        {directoryFeedback ? (
          <div className="gg-alert-stack gg-workers-state-stack">
            <p className="gg-inline-alert gg-inline-alert--error">{directoryFeedback}</p>
          </div>
        ) : null}

        {loading ? (
          <LoadingState title="Loading workers" message="Syncing worker records and status details." />
        ) : null}

        {!loading && loadError ? (
          <ErrorState
            title="Workers unavailable"
            message={loadError}
            action={(
              <button type="button" className="gg-button gg-button--secondary" onClick={() => loadWorkers()}>
                Retry
              </button>
            )}
          />
        ) : null}

        {!loading && !loadError && workers.length === 0 ? (
          <EmptyState
            title="No workers yet"
            message="Create your first worker record to start managing workforce access and status."
            action={(
              <button type="button" className="gg-button gg-button--primary" onClick={openCreate}>
                Add Worker
              </button>
            )}
          />
        ) : null}

        {!loading && !loadError && workers.length > 0 ? (
          <TemplateTableShell className="gg-workers-table-wrap">
            <table className="gg-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {workers.map((worker) => {
                  const isActive = worker.is_active !== false

                  return (
                    <tr key={worker.id}>
                      <td className="gg-workers-cell gg-workers-cell--name">
                        <span className="gg-workers-cell__value">{worker.name || 'Unnamed Worker'}</span>
                      </td>
                      <td className="gg-workers-cell gg-workers-cell--phone">
                        <span className="gg-workers-cell__value">{worker.phone || 'N/A'}</span>
                      </td>
                      <td className="gg-workers-cell gg-workers-cell--role">
                        <span className="gg-workers-cell__value">{formatRoleLabel(worker.role)}</span>
                      </td>
                      <td className="gg-workers-cell gg-workers-cell--status">
                        <StatusBadge status={isActive ? 'success' : 'paused'} label={isActive ? 'Active' : 'Inactive'} />
                      </td>
                      <td className="gg-workers-cell gg-workers-cell--actions">
                        <div className="gg-workers-row-actions">
                          <button type="button" className="gg-button gg-button--secondary" onClick={() => openEdit(worker)}>
                            Edit
                          </button>
                          {isActive ? (
                            <button
                              type="button"
                              className="gg-button gg-button--danger"
                              onClick={() => handleDeactivate(worker.id)}
                              disabled={deactivateBusyWorkerId === worker.id}
                            >
                              {deactivateBusyWorkerId === worker.id ? 'Deactivating...' : 'Deactivate'}
                            </button>
                          ) : (
                            <span className="gg-workers-row-note">Already inactive</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </TemplateTableShell>
        ) : null}
      </TemplateSectionCard>
    </TemplatePage>
  )
}
